import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import { listSitesLite, listBaysLite } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * /admin/email-handling — Phase 9.4
 *
 * Manage additional CC recipients for outbound booking emails.
 * Two scoping tiers:
 *   • Client-Site: CC applies to every booking for a given site
 *   • Site-Bay:    CC applies only to that specific bay's bookings
 *
 * Each row can optionally target specific role-flavoured templates
 * (booker / site_admin / dock_manager / driver / client_admin) via
 * a role multiselect. Empty roles = all templates.
 */

const ROLE_CHOICES = [
  { value: "booker",       label: "All client users (booker)" },
  { value: "site_admin",   label: "Site admin" },
  { value: "dock_manager", label: "Dock manager" },
  { value: "client_admin", label: "Client admin" },
  { value: "driver",       label: "Driver" },
];

// Phase 9.8 — categories the AFTER UPDATE trigger watches for material
// field changes. Keys MUST match the boolean columns on
// lbs_site_update_email_prefs and the CASE branches in
// lbs_booking_changed_categories() in migration 090.
const UPDATE_CATEGORIES = [
  { key: "watch_datetime",   label: "Date / Time",          hint: "Start or end time changed" },
  { key: "watch_site_bay",   label: "Site / Bay",           hint: "Site or bay reassigned" },
  { key: "watch_driver",     label: "Driver",               hint: "Driver assignment or TBA flag" },
  { key: "watch_customer",   label: "Customer",             hint: "Sender / receiver / other party" },
  { key: "watch_product",    label: "Product / Load",       hint: "Goods description, pallets, weight, volume, qty" },
  { key: "watch_vehicle",    label: "Vehicle",              hint: "Vehicle type, regos, mass limits" },
  { key: "watch_restraint",  label: "Restraint",            hint: "Restraint method, straps/chains/boards counts" },
  { key: "watch_compliance", label: "Compliance",           hint: "Cold chain, food safety, DG, temp confirmations" },
];

export default function AdminEmailHandling() {
  const { assignments, hasRole } = useAuth();
  const { viewAsClientId, isSuperAdmin } = useViewAsClient();

  const clientId = isSuperAdmin
    ? (viewAsClientId || null)
    : (assignments?.[0]?.client_id || null);

  const [sites, setSites] = useState([]);
  const [rows, setRows] = useState([]);
  const [updatePrefs, setUpdatePrefs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    const [sitesRes, ccRes, prefsRes] = await Promise.all([
      listSitesLite(clientId),
      supabase.from("lbs_email_cc_recipients").select("*").eq("client_id", clientId).order("created_at"),
      supabase.from("lbs_site_update_email_prefs").select("*").eq("client_id", clientId),
    ]);
    setSites(sitesRes.data || []);
    setRows(ccRes.data || []);
    setUpdatePrefs(prefsRes.data || []);
    setLoading(false);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const sitesById = useMemo(() => {
    const m = new Map();
    sites.forEach((s) => m.set(s.id, s));
    return m;
  }, [sites]);

  const [baysBySite, setBaysBySite] = useState({});
  useEffect(() => {
    (async () => {
      const out = {};
      for (const s of sites) {
        const { data } = await listBaysLite(s.id);
        out[s.id] = data || [];
      }
      setBaysBySite(out);
    })();
  }, [sites]);

  const addRow = async (scope) => {
    if (!clientId || !sites.length) {
      toast.error("Pick a client with at least one site first.");
      return;
    }
    const payload = {
      client_id: clientId,
      scope,
      site_id: sites[0].id,
      bay_id: null,
      cc_email: "",
      roles: [],
      is_active: true,
    };
    if (scope === "site_bay") {
      const bays = baysBySite[sites[0].id] || [];
      if (!bays.length) { toast.error("First site has no bays."); return; }
      payload.bay_id = bays[0].id;
    }
    // Insert as draft (empty email is invalid at DB level so keep local until user fills it).
    setRows((r) => [...r, { ...payload, id: `draft-${Date.now()}`, _isDraft: true }]);
  };

  const patchRow = (id, patch) => {
    setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  };

  const saveRow = async (row) => {
    if (!row.cc_email || !row.cc_email.includes("@")) {
      toast.error("Enter a valid email address."); return;
    }
    if (!row.site_id) { toast.error("Pick a site."); return; }
    if (row.scope === "site_bay" && !row.bay_id) { toast.error("Pick a bay."); return; }

    const body = {
      client_id: row.client_id,
      scope: row.scope,
      site_id: row.site_id,
      bay_id: row.scope === "site_bay" ? row.bay_id : null,
      cc_email: row.cc_email.trim(),
      roles: row.roles || [],
      is_active: row.is_active !== false,
    };
    if (row._isDraft) {
      const { data, error } = await supabase.from("lbs_email_cc_recipients").insert(body).select().single();
      if (error) { toast.error(error.message); return; }
      setRows((r) => r.map((x) => (x.id === row.id ? data : x)));
      toast.success("Added CC");
    } else {
      const { data, error } = await supabase.from("lbs_email_cc_recipients").update(body).eq("id", row.id).select().single();
      if (error) { toast.error(error.message); return; }
      setRows((r) => r.map((x) => (x.id === row.id ? data : x)));
      toast.success("Updated CC");
    }
  };

  const deleteRow = async (row) => {
    if (row._isDraft) { setRows((r) => r.filter((x) => x.id !== row.id)); return; }
    if (!window.confirm(`Remove ${row.cc_email}?`)) return;
    const { error } = await supabase.from("lbs_email_cc_recipients").delete().eq("id", row.id).select();
    if (error) { toast.error(error.message); return; }
    setRows((r) => r.filter((x) => x.id !== row.id));
    toast.success("Removed");
  };

  const toggleRole = (row, r) => {
    const cur = row.roles || [];
    const next = cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r];
    patchRow(row.id, { roles: next });
  };

  // Phase 9.8 — Per-site update-email preferences.
  const prefsBySite = useMemo(() => {
    const m = new Map();
    updatePrefs.forEach((p) => m.set(p.site_id, p));
    return m;
  }, [updatePrefs]);

  const saveSitePrefs = async (siteId, patch) => {
    if (!clientId) return;
    const existing = prefsBySite.get(siteId);
    const body = {
      client_id: clientId,
      site_id: siteId,
      watch_datetime:   existing?.watch_datetime   ?? true,
      watch_site_bay:   existing?.watch_site_bay   ?? true,
      watch_driver:     existing?.watch_driver     ?? true,
      watch_customer:   existing?.watch_customer   ?? false,
      watch_product:    existing?.watch_product    ?? false,
      watch_vehicle:    existing?.watch_vehicle    ?? false,
      watch_restraint:  existing?.watch_restraint  ?? false,
      watch_compliance: existing?.watch_compliance ?? false,
      ...patch,
    };
    // upsert on (client_id, site_id)
    const { data, error } = await supabase
      .from("lbs_site_update_email_prefs")
      .upsert(body, { onConflict: "client_id,site_id" })
      .select()
      .single();
    if (error) { toast.error(error.message); return; }
    setUpdatePrefs((p) => {
      const idx = p.findIndex((x) => x.site_id === siteId);
      if (idx === -1) return [...p, data];
      const next = [...p]; next[idx] = data; return next;
    });
    toast.success("Update preferences saved");
  };

  if (!hasRole("super_admin") && !hasRole("client_admin") && !hasRole("site_admin")) {
    return <p className="text-sm text-rose-700 p-4">Admins only.</p>;
  }

  const clientSiteRows = rows.filter((r) => r.scope === "client_site");
  const siteBayRows    = rows.filter((r) => r.scope === "site_bay");

  return (
    <div className="space-y-4" data-testid="admin-email-handling-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Mail className="h-6 w-6" /> Email Handling
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Add extra CC recipients for outbound booking notifications. Scope
          at client-site or bay level. Optional role filter — leave empty
          to CC on every template.
        </p>
      </div>

      {!clientId && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          {isSuperAdmin
            ? "Pick a client from the top-bar 'View as client' picker to configure Email Handling."
            : "No tenant assignment on your account."}
        </div>
      )}

      {clientId && (
        <>
          <UpdatePrefsCard sites={sites} prefsBySite={prefsBySite} onSave={saveSitePrefs} />

          <CcCard title="Client-Site CCs"
                  description="Applies to every booking at the selected site."
                  scope="client_site"
                  rows={clientSiteRows}
                  sites={sites} baysBySite={baysBySite} sitesById={sitesById}
                  onAdd={() => addRow("client_site")}
                  onPatch={patchRow} onSave={saveRow} onDelete={deleteRow}
                  onToggleRole={toggleRole}
                  testidPrefix="cs" />

          <CcCard title="Site-Bay CCs"
                  description="Applies only to bookings at the selected bay."
                  scope="site_bay"
                  rows={siteBayRows}
                  sites={sites} baysBySite={baysBySite} sitesById={sitesById}
                  onAdd={() => addRow("site_bay")}
                  onPatch={patchRow} onSave={saveRow} onDelete={deleteRow}
                  onToggleRole={toggleRole}
                  testidPrefix="sb" />

          {loading && <p className="text-xs text-slate-400">Loading…</p>}
        </>
      )}
    </div>
  );
}

function CcCard({ title, description, scope, rows, sites, baysBySite, sitesById,
                 onAdd, onPatch, onSave, onDelete, onToggleRole, testidPrefix }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="text-xs text-slate-500 mt-1">{description}</p>
        </div>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`${testidPrefix}-add-btn`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add CC
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length === 0 && (
          <p className="text-xs text-slate-400 italic" data-testid={`${testidPrefix}-empty`}>
            No CCs yet. Use &quot;Add CC&quot; to configure one.
          </p>
        )}
        {rows.map((r) => (
          <div key={r.id}
               className={`p-2 rounded border ${r._isDraft ? "border-amber-300 bg-amber-50/40" : "border-slate-200"} space-y-2`}
               data-testid={`${testidPrefix}-row-${r.id}`}>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
              <div className="sm:col-span-4">
                <label className="text-[11px] uppercase tracking-wider text-slate-500">CC email</label>
                <Input value={r.cc_email || ""}
                       onChange={(e) => onPatch(r.id, { cc_email: e.target.value })}
                       placeholder="e.g. luis@example.com"
                       className="h-9"
                       data-testid={`${testidPrefix}-email-${r.id}`} />
              </div>
              <div className="sm:col-span-3">
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Site</label>
                <Select value={r.site_id || ""} onValueChange={(v) => onPatch(r.id, { site_id: v, bay_id: null })}>
                  <SelectTrigger className="h-9" data-testid={`${testidPrefix}-site-${r.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {scope === "site_bay" && (
                <div className="sm:col-span-3">
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">Bay</label>
                  <Select value={r.bay_id || ""} onValueChange={(v) => onPatch(r.id, { bay_id: v })}>
                    <SelectTrigger className="h-9" data-testid={`${testidPrefix}-bay-${r.id}`}>
                      <SelectValue placeholder="Pick bay" />
                    </SelectTrigger>
                    <SelectContent>
                      {(baysBySite[r.site_id] || []).map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className={`${scope === "site_bay" ? "sm:col-span-1" : "sm:col-span-4"} flex items-center gap-2 justify-end`}>
                <Switch checked={r.is_active !== false}
                        onCheckedChange={(v) => onPatch(r.id, { is_active: v })}
                        data-testid={`${testidPrefix}-active-${r.id}`}
                        title={r.is_active !== false ? "Active" : "Paused"} />
                <Button size="sm" onClick={() => onSave(r)} data-testid={`${testidPrefix}-save-${r.id}`}>
                  Save
                </Button>
                <Button size="icon" variant="ghost" onClick={() => onDelete(r)}
                        data-testid={`${testidPrefix}-delete-${r.id}`}>
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 pl-1">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-1">
                CC when notifying:
              </span>
              {ROLE_CHOICES.map((rc) => {
                const active = (r.roles || []).includes(rc.value);
                return (
                  <button key={rc.value}
                          type="button"
                          onClick={() => onToggleRole(r, rc.value)}
                          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                            active
                              ? "bg-sky-100 border-sky-300 text-sky-800"
                              : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                          }`}
                          data-testid={`${testidPrefix}-role-${rc.value}-${r.id}`}>
                    {rc.label}
                  </button>
                );
              })}
              {(!r.roles || r.roles.length === 0) && (
                <Badge variant="outline" className="text-[10px] ml-1">every template</Badge>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Phase 9.8 — Per-site preferences that gate the `booking_updated`
 * email. One row per site; each row has 8 category toggles. If ANY
 * category is enabled for the site and a matching field is edited on
 * a live booking, the outbox drainer sends an update notification
 * (inheriting the site/bay/role CC list configured below).
 */
function UpdatePrefsCard({ sites, prefsBySite, onSave }) {
  return (
    <Card data-testid="update-prefs-card">
      <CardHeader>
        <CardTitle className="text-base">Booking-update notifications</CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Choose per site which changes to a saved booking should trigger
          an update email. The booker (and any CCs configured below) will
          receive a fresh confirmation with the updated details and link
          whenever the selected fields change. Tick nothing to disable.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {sites.length === 0 && (
          <p className="text-xs text-slate-400 italic">No sites configured for this client.</p>
        )}
        {sites.map((s) => {
          const prefs = prefsBySite.get(s.id);
          return (
            <div key={s.id}
                 className="p-3 rounded border border-slate-200 space-y-2"
                 data-testid={`prefs-row-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">{s.name}</div>
                <span className="text-[11px] text-slate-400">
                  {prefs ? "Configured" : "Defaults (Date/Time, Site/Bay, Driver)"}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {UPDATE_CATEGORIES.map((c) => {
                  const val = prefs ? !!prefs[c.key] : (
                    c.key === "watch_datetime" || c.key === "watch_site_bay" || c.key === "watch_driver"
                  );
                  return (
                    <label key={c.key}
                           className="flex items-start gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-slate-50"
                           title={c.hint}
                           data-testid={`prefs-${s.id}-${c.key}-label`}>
                      <Switch checked={val}
                              onCheckedChange={(v) => onSave(s.id, { [c.key]: v })}
                              data-testid={`prefs-${s.id}-${c.key}`} />
                      <span className="pt-0.5">
                        <span className="block font-medium text-slate-700">{c.label}</span>
                        <span className="block text-[10px] text-slate-400 leading-tight">{c.hint}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

